/**
 * clasificadores/estrategiaFlex.js — Clasificador puro del dashboard
 * Estrategia Flex (réplica del modelo, 12-jul-2026).
 *
 * Las ZONAS no se inventan: son las que trae Mercado Libre en el sync
 * (mlFlexConfig → coverage.byZone). El item de cada tarea ES la zona.
 *
 * 3 secciones (decisión Mariano 12-jul):
 *   activar_zona        → zonas Flex APAGADAS con ventas en la ventana
 *                         (≥ piso de órdenes, default 5) → activar_zona_flex.
 *                         Orden: facturación DESC.
 *   ampliar_horario     → zonas PRENDIDAS con órdenes creadas DESPUÉS del
 *                         cutoff actual de ML y ANTES del tope del operador
 *                         (⚙️ Operación por zona: cutoffTope, default 16:00).
 *                         Nunca sugiere más allá del tope (la capacidad la
 *                         define el operador de CADA zona — independientes).
 *                         Si la zona tiene capacidadPaquetes y el promedio
 *                         diario Flex ya la llena → no sugiere (declarado).
 *                         → ampliar_horario_flex.
 *   revisar_performance → is_downgraded de ML activo → UNA tarea de gestión
 *                         (revisar_performance_flex, esfuerzo 4, 🛡).
 *
 * Atribución orden → zona (espejo de FlexZonasDashboard.zonaEstado):
 *   1) point-in-polygon con el GeoJSON de la zona (lat/lng del enricher)
 *   2) fallback por nombre de barrio/municipio (zone.neighborhoods)
 *   3) sin match → declarada (sinZona), no se inventa.
 *
 * Input: { flexConfig (doc mlFlexConfig crudo), ventas30d (shape cascada),
 *          config (doc empresa — flexConfig.zonasOperacion),
 *          ventanaVentasDias (días con los que se cargó `ventas30d`) }
 *
 * La ventana entra SÓLO en la compuerta de capacidad de ampliar_horario
 * (promedio diario Flex = órdenes Flex / ventana) y en los textos. Sin ventana
 * (report djynjlXICuUF0Yl803M8; decisión de Mariano del 11-oct-2026, pregunta
 * 29: «deja de calcular y avisa»): una zona CON capacidad configurada no se
 * sugiere —no se puede saber si ya está llena—, se cuenta en
 * `excluidos.capacidadSinVentana` y se declara en `lecturas`; las zonas sin
 * capacidad, activar_zona y revisar_performance no usan la ventana y siguen
 * igual.
 */

import { findZonaForPoint } from '@core/lib/geometria'
import { ventanaDeVentas, FUENTE_VENTANA } from './ventanaDeVentas'

export const CUTOFF_TOPE_DEFAULT_HORA = 16   // ⚙️ zona sin setup → corte 16:00

// ── Helpers ──
// "13" / 13 / "13:30" / {week, saturday, sunday} → hora decimal (13.5) | null
export function parseHora(v) {
  if (v == null) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const m = v.trim().match(/^(\d{1,2})(?::(\d{2}))?$/)
    if (!m) return null
    return Number(m[1]) + (m[2] ? Number(m[2]) / 60 : 0)
  }
  if (typeof v === 'object') return parseHora(v.week ?? v.weekdays ?? null)
  return null
}

const fmtHora = (h) => {
  if (h == null) return '—'
  const hh = Math.floor(h)
  const mm = Math.round((h - hh) * 60)
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

// Normalización de nombres — ESPEJO de norm() de FlexZonasApagadasDashboard:
// underscores → espacio, sufijo numérico ("La Matanza 1"), tildes, y el alias
// CABA == Capital Federal == Ciudad Autónoma de Buenos Aires.
function normNombre(s) {
  if (!s) return ''
  let r = String(s).toLowerCase()
  r = r.normalize('NFD').replace(/[̀-ͯ]/g, '')
  r = r.replace(/_/g, ' ')
  r = r.replace(/\s+\d+$/, '')
  r = r.replace(/\s+/g, ' ').trim()
  if (r === 'capital federal' || r === 'ciudad autonoma de buenos aires' || r === 'ciudad de buenos aires') return 'caba'
  return r
}

function extractAddress(orden) {
  const addr = orden?.shipping?.receiver_address || {}
  const enrichedAddr = orden?._enriched?.shipment?.receiverAddress || {}
  const provincia = addr.state?.name || addr.state?.id || enrichedAddr.state || enrichedAddr.stateName || null
  const municipio = addr.city?.name || addr.city?.id || enrichedAddr.city || enrichedAddr.cityName || null
  const latRaw = addr.latitude ?? enrichedAddr.latitude ?? null
  const lngRaw = addr.longitude ?? enrichedAddr.longitude ?? null
  return { provincia, municipio, lat: latRaw != null ? Number(latRaw) : NaN, lng: lngRaw != null ? Number(lngRaw) : NaN }
}

const esFlexOrden = (v) =>
  v?._enriched?.shipment?.logisticType === 'self_service'
  || v?.shipping?.logisticType === 'self_service'
  || v?.shipping?.logistic_type === 'self_service'

export function clasificarEstrategiaFlex({
  flexConfig = null,
  ventas30d = [],
  config = {},
  // La ventana con la que el llamador cargó `ventas30d` (el loader del agente
  // la publica en `datos.ventanaVentasDias`; la vista la pasa con la misma
  // constante de su query). Los umbrales del agente ya no se leen para esto.
  ventanaVentasDias = null,
} = {}) {
  const ventanaDias = ventanaDeVentas(ventanaVentasDias)
  // «en 30d» con ventana; sin ventana el texto no inventa un número.
  const enVentana = ventanaDias === null ? 'en la ventana' : `en ${ventanaDias}d`
  const zonasOperacion = config?.flexConfig?.zonasOperacion || {}

  const cov = flexConfig?.coverage || {}
  const zones = Array.isArray(cov.byZone) ? cov.byZone.filter(z => z?.id != null) : []

  // Polígonos parseados (mismo criterio que la vista Zonas).
  const zonesParsed = []
  for (const z of zones) {
    if (!z.polygonStr) continue
    try {
      const polygon = JSON.parse(z.polygonStr)
      if (polygon) zonesParsed.push({ ...z, polygon })
    } catch { /* polígono inválido */ }
  }

  // Índice barrio/municipio normalizado → zona (fallback sin coordenadas).
  const muniAZona = new Map()
  const zonesById = new Map(zones.map(z => [String(z.id), z]))
  for (const z of zones) {
    for (const n of (z.neighborhoods || [])) {
      const k = normNombre(n?.name)
      if (k && !muniAZona.has(k)) muniAZona.set(k, z)
    }
    const kLabel = normNombre(z.label)
    if (kLabel && !muniAZona.has(kLabel)) muniAZona.set(kLabel, z)
  }
  // + coverage.byMunicipio: el sync guarda el link municipio → zonaId (mismo
  // índice que usan los dashboards de Zonas para matchear por nombre). Es el
  // camino que resuelve GBA, donde el label de la zona no es el municipio.
  const byMuni = cov.byMunicipio || {}
  for (const info of Object.values(byMuni)) {
    if (!info?.zonaId) continue
    const zona = zonesById.get(String(info.zonaId))
    if (!zona) continue
    const k = normNombre(info.municipio)
    if (k && !muniAZona.has(k)) muniAZona.set(k, zona)
  }

  // Cutoff GLOBAL de ML (fallback cuando la zona no trae el suyo).
  const drWeek = flexConfig?.deliveryRanges?.week || []
  const cutsGlobales = drWeek.map(r => parseHora(r?.cutoff)).filter(c => c != null)
  const cutoffGlobal = cutsGlobales.length > 0
    ? cutsGlobales.slice().sort((a, b) => a - b)[Math.floor(cutsGlobales.length / 2)]
    : null

  // ── Stats por zona (una pasada sobre las ventas) ──
  const statsPorZona = new Map()  // zonaId → { ordenes, unidades, facturacion, ordenesFlex, porHora[24] }
  let sinZona = 0
  const sinZonaMunis = new Map()  // municipio crudo → count (diagnóstico declarado)
  const statsDe = (zonaId) => {
    let s = statsPorZona.get(zonaId)
    if (!s) { s = { ordenes: 0, unidades: 0, facturacion: 0, ordenesFlex: 0, porHora: Array(24).fill(0), facPorHora: Array(24).fill(0) }; statsPorZona.set(zonaId, s) }
    return s
  }

  for (const v of ventas30d) {
    if (v?.estado === 'cancelada') continue
    const { municipio, lat, lng } = extractAddress(v)
    let zona = null
    if (Number.isFinite(lat) && Number.isFinite(lng) && zonesParsed.length > 0) {
      zona = findZonaForPoint([lng, lat], zonesParsed)
    }
    if (!zona && municipio) zona = muniAZona.get(normNombre(municipio)) || null
    if (!zona) {
      sinZona++
      const k = municipio || '(sin municipio en la orden)'
      sinZonaMunis.set(k, (sinZonaMunis.get(k) || 0) + 1)
      continue
    }

    const s = statsDe(String(zona.id))
    const unidades = (v.order_items || []).reduce((a, oi) => a + Number(oi.quantity || 0), 0)
    const revenue = (v.order_items || []).reduce((a, oi) => a + Number(oi.quantity || 0) * Number(oi.unit_price || 0), 0)
    s.ordenes += 1
    s.unidades += unidades
    s.facturacion += revenue
    if (esFlexOrden(v)) s.ordenesFlex += 1
    const fecha = v._dateCreated instanceof Date ? v._dateCreated : (v.date_created ? new Date(v.date_created) : null)
    if (fecha && !isNaN(fecha.getTime())) {
      const h = fecha.getHours()
      s.porHora[h] += 1
      s.facPorHora[h] += revenue
    }
  }

  // ── Secciones ──
  const activar_zona = []
  const ampliar_horario = []
  const revisar_performance = []
  let apagadasConVentas = 0
  let zonasExcluidas = 0
  let zonasAlTope = 0
  let capacidadLlena = 0
  let capacidadSinVentana = 0
  let zonasSinCutoff = 0
  let ordenesFueraDeTope = 0

  for (const z of zones) {
    const zonaId = String(z.id)
    const s = statsPorZona.get(zonaId) || null
    const cfgZona = zonasOperacion[zonaId] || {}

    // Excluida por decisión (⚙️ Operación por zona): "acá no queremos estar".
    // No se sugiere NADA para esta zona — ni activar ni ampliar. Declarada.
    if (cfgZona.excluida === true) { zonasExcluidas++; continue }

    if (!z.enabled) {
      // ── ACTIVAR ZONA: TODAS las apagadas (decisión Mariano 12-jul) ──
      // Prender una zona es un quick win sin costo: la tarea es contra las
      // ZONAS, no contra las órdenes. Las ventas ordenan la prioridad, no
      // filtran — las que tienen plata esperando van primero.
      const ordenes = s?.ordenes || 0
      if (ordenes > 0) apagadasConVentas++
      activar_zona.push({
        id: zonaId,
        itemId: zonaId,
        _itemId: zonaId,
        title: `Zona Flex: ${z.label || zonaId}`,
        _zonaLabel: z.label || zonaId,
        _ordenes30d: ordenes,
        _unidades30d: s?.unidades || 0,
        _facturacion30d: s?.facturacion || 0,
        _motivo: ordenes > 0
          ? `Zona apagada con ${ordenes} órdenes ${enVentana} ($${Math.round(s.facturacion).toLocaleString('es-AR')}) — activando Flex entregás en el día`
          : 'Zona apagada sin ventas en la ventana — sumar cobertura Flex',
        metricas: {
          zonaId, zonaLabel: z.label || zonaId,
          ordenes30d: ordenes, unidades30d: s?.unidades || 0,
          facturacion30d: Math.round(s?.facturacion || 0),
        },
      })
      continue
    }

    // ── AMPLIAR HORARIO: prendida, órdenes entre el cutoff ML y el tope ──
    const cutoffML = parseHora(z.cutoff) ?? cutoffGlobal
    if (cutoffML == null) { zonasSinCutoff++; continue }
    const tope = parseHora(cfgZona.cutoffTope) ?? CUTOFF_TOPE_DEFAULT_HORA
    // fuera de tope se declara siempre (aunque la zona esté al tope)
    if (s) {
      for (let h = Math.ceil(tope); h < 24; h++) ordenesFueraDeTope += s.porHora[h]
    }
    if (cutoffML >= tope) { zonasAlTope++; continue }

    // Capacidad del operador: si el promedio diario Flex ya la llena, no sugerimos.
    const capacidad = Number(cfgZona.capacidadPaquetes) > 0 ? Number(cfgZona.capacidadPaquetes) : null
    if (capacidad != null && ventanaDias === null) { capacidadSinVentana++; continue }
    const promedioDiarioFlex = ventanaDias === null ? null : (s?.ordenesFlex || 0) / ventanaDias
    if (capacidad != null && promedioDiarioFlex >= capacidad) { capacidadLlena++; continue }

    // THRESHOLD (decisión Mariano 12-jul): corte actual < tope del operador →
    // se sugiere ampliar SIEMPRE. Más horario = más posibilidad de venta
    // same-day. Las órdenes históricas en la franja NO filtran: son contexto
    // y prioridad (las zonas con plata concreta esperando van primero).
    let capturables = 0
    let facCapturable = 0
    if (s) {
      for (let h = Math.floor(cutoffML); h < Math.ceil(tope) && h < 24; h++) {
        // borde: si el cutoff es 13:30, la franja de las 13 cuenta entera (aprox declarada)
        capturables += s.porHora[h]
        facCapturable += s.facPorHora[h]
      }
    }

    ampliar_horario.push({
      id: zonaId,
      itemId: zonaId,
      _itemId: zonaId,
      title: `Zona Flex: ${z.label || zonaId}`,
      _zonaLabel: z.label || zonaId,
      _cutoffActual: cutoffML,
      _cutoffTope: tope,
      _capturables30d: capturables,
      _facCapturable30d: facCapturable,
      _ordenes30d: s?.ordenes || 0,
      _motivo: capturables > 0
        ? `Ampliar el corte de ${fmtHora(cutoffML)} a ${fmtHora(tope)} (tope del operador) — capturás ~${capturables} órdenes ${enVentana} ($${Math.round(facCapturable).toLocaleString('es-AR')}) que hoy entran después del cutoff`
        : `Ampliar el corte de ${fmtHora(cutoffML)} a ${fmtHora(tope)} (tope del operador) — más horario de corte, más posibilidad de venta same-day`,
      metricas: {
        zonaId, zonaLabel: z.label || zonaId,
        cutoffActual: fmtHora(cutoffML), cutoffTope: fmtHora(tope),
        capturables30d: capturables, facturacionCapturable30d: Math.round(facCapturable),
        // null sin ventana: `Math.round(null * 10)` daría 0, que se lee como respuesta.
        promedioDiarioFlex: promedioDiarioFlex === null ? null : Math.round(promedioDiarioFlex * 10) / 10,
        capacidadPaquetes: capacidad,
      },
    })
  }

  activar_zona.sort((a, b) => (b._facturacion30d || 0) - (a._facturacion30d || 0))
  ampliar_horario.sort((a, b) => (b._facCapturable30d || 0) - (a._facCapturable30d || 0))

  // ── REVISAR PERFORMANCE: downgrade de ML ──
  if (flexConfig?.isDowngraded) {
    const subs = (flexConfig.subscriptions || []).map(su => ({ serviceId: su.serviceId ?? null, status: su.status ?? null, mode: su.mode ?? null }))
    revisar_performance.push({
      id: 'flex_downgrade',
      itemId: 'flex_downgrade',
      _itemId: 'flex_downgrade',
      title: 'Performance Flex degradada (is_downgraded)',
      _motivo: 'Mercado Libre degradó la performance de Flex de la cuenta — revisar causas (entregas tarde, cancelaciones) y plan de recuperación con el operador.',
      metricas: { isDowngraded: true, subscriptions: subs },
    })
  }

  return {
    secciones: { activar_zona, ampliar_horario, revisar_performance },
    analisis: {
      ventanaDias,   // descriptor que lee el contrato del registry
      kpis: {
        activarZona: activar_zona.length,
        ampliarHorario: ampliar_horario.length,
        revisarPerformance: revisar_performance.length,
        zonasML: zones.length,
      },
    },
    excluidos: {
      cutoffTopeDefault: fmtHora(CUTOFF_TOPE_DEFAULT_HORA),
      sinZona,
      apagadasConVentas,
      zonasExcluidas,
      zonasAlTope,
      capacidadLlena,
      capacidadSinVentana,
      zonasSinCutoff,
      ordenesFueraDeTope,
      // Diagnóstico declarado: los 5 municipios con más órdenes sin zona —
      // para ajustar el matcheo con datos y no a ciegas.
      sinZonaTop: Array.from(sinZonaMunis.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([muni, n]) => `${muni} (${n})`),
    },
    // El canal clasificador → motor (molde: velocidadConsumo). SÓLO si sin
    // ventana se dejó de sugerir algo: si ninguna zona tiene capacidad, no se
    // perdió nada calculable y la corrida no es degradada.
    ...(capacidadSinVentana > 0 ? {
      lecturas: {
        fuentes: [FUENTE_VENTANA],
        degradaciones: [{ fuente: FUENTE_VENTANA, motivo: `sin-dato: no se sabe con cuántos días de ventas se cargaron los datos — ${capacidadSinVentana} zona(s) con capacidad configurada sin evaluar si ya está llena` }],
      },
    } : {}),
  }
}
